/**
 * Star ratings for one list: a single read for every item on screen, and a vote that shows at once
 * (worked out from the mean, withVote) and is then replaced by the api's answer in every list holding
 * that item. An item not yet read, or out of reach, reads as unrated.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { UNRATED, ratingKey, withVote, type Rating, type RatingTarget } from '@studio/contracts';
import { queryClient, trpc } from '@/lib/trpc';

type RatingMap = Record<string, Rating>;

function patchEverywhere(key: string, next: (prev: Rating) => Rating) {
  queryClient.setQueriesData<RatingMap>({ queryKey: trpc.ratings.of.pathKey() }, (map) => (map && key in map ? { ...map, [key]: next(map[key]!) } : map));
}

export function useRatings(targets: RatingTarget[]) {
  const ratings = useQuery(trpc.ratings.of.queryOptions({ targets }, { enabled: targets.length > 0, placeholderData: (p) => p }));
  const rate = useMutation(trpc.ratings.rate.mutationOptions({
    onMutate: ({ target, stars }) => patchEverywhere(ratingKey(target), (prev) => withVote(prev, stars)),
    onSuccess: (rating, { target }) => patchEverywhere(ratingKey(target), () => rating),
    onError: () => void queryClient.invalidateQueries({ queryKey: trpc.ratings.of.pathKey() }),
  }));
  return {
    ratingOf: (target: RatingTarget): Rating => ratings.data?.[ratingKey(target)] ?? UNRATED,
    rate: (target: RatingTarget, stars: number | null) => rate.mutate({ target, stars }),
  };
}
